import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import { SummaryPanel } from '~/pages/buy-lists/sections/SummaryPanel';

export type SummaryProduct = {
  id: string;
  name: string;
  lengthLabel?: string;
  quantityLabel: ReactNode;
  priceLabel?: string;
  tallyKind?: 'canned' | 'random';
};

type SummaryProductsPanelProps = {
  title: string;
  products: SummaryProduct[];
  testIdPrefix?: string;
};

function testIdFor(testIdPrefix: string | undefined, suffix: string) {
  return testIdPrefix === undefined ? undefined : `${testIdPrefix}-${suffix}`;
}

const TALLY_FLAG_LABEL_KEYS = {
  canned: 'inventory.buy_list.tallies.canned_flag',
  random: 'inventory.buy_list.tallies.random_flag',
} as const;

export function SummaryProductsPanel({
  title,
  products,
  testIdPrefix,
}: Readonly<SummaryProductsPanelProps>) {
  const { t } = useTranslation('inventory');
  return (
    <SummaryPanel
      title={title}
      count={products.length}
      icon={<Package size={16} />}
    >
      <ul
        className="flex flex-col gap-1"
        data-testid={testIdFor(testIdPrefix, 'products')}
      >
        {products.map((product) => (
          <li
            key={product.id}
            data-testid={testIdFor(testIdPrefix, `product-${product.id}`)}
            className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-neut-50"
          >
            <span className="flex min-w-0 flex-1 items-baseline gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-neut-prim">
                {product.name}
              </span>
              {product.tallyKind !== undefined && (
                <span
                  data-testid={testIdFor(testIdPrefix, 'tally-flag')}
                  className="shrink-0 rounded bg-status-info-50 px-1.5 text-xs font-semibold text-status-info-800"
                >
                  {t(TALLY_FLAG_LABEL_KEYS[product.tallyKind])}
                </span>
              )}
              {product.lengthLabel !== undefined && (
                <span className="shrink-0 text-xs text-neut-sec">
                  {product.lengthLabel}
                </span>
              )}
            </span>
            <span className="shrink-0 rounded-md bg-neut-100 px-2 py-1 text-xs font-semibold text-neut-prim tabular-nums">
              {product.quantityLabel}
            </span>
            {product.priceLabel && (
              <span className="shrink-0 text-xs text-neut-sec tabular-nums">
                {product.priceLabel}
              </span>
            )}
          </li>
        ))}
      </ul>
    </SummaryPanel>
  );
}
